/**
 * The slice of the web app's agents API client the pane uses: file uploads
 * for the thread sandbox and download links for them. Both live on the REST
 * API (not Aegra), under `/agents/threads/{id}/uploads`.
 *
 * The web app wraps these in TanStack Query mutations; the pane calls them
 * directly.
 */
import axios from "../../taskpane/helper/axios";
import { useCallback, useState } from "react";

import type { AgentFileLink, AgentUploadedFile, ThreadFileInput, UploadFilesInput } from "@/agents-v2/api/types";

export type { AgentFileLink, AgentUploadedFile, ThreadFileInput, UploadFilesInput } from "@/agents-v2/api/types";

type UploadResponse = {
  files?: AgentUploadedFile[];
};

const agentsApiUrl = (path: string): string => {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  return `${base}${path}`;
};

const authHeaders = (token: string | null | undefined) => ({
  Authorization: `Bearer ${token ?? ""}`,
});

export const uploadAgentFiles = async ({
  threadId,
  files,
  token,
  signal,
}: UploadFilesInput & {
  token: string | null | undefined;
}): Promise<AgentUploadedFile[]> => {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file, file.name);
  }

  const { data } = await axios.post<UploadResponse>(
    agentsApiUrl(`/agents/threads/${encodeURIComponent(threadId)}/uploads`),
    formData,
    { headers: authHeaders(token), signal }
  );

  if (!Array.isArray(data.files)) {
    throw new Error("Uploaded files missing from response.");
  }

  return data.files;
};

export const createArtifactLink = async ({
  threadId,
  filename,
  token,
}: ThreadFileInput & {
  token: string | null | undefined;
}): Promise<AgentFileLink> => {
  const { data } = await axios.get<Partial<AgentFileLink>>(
    agentsApiUrl(`/agents/threads/${encodeURIComponent(threadId)}/artifacts?filename=${encodeURIComponent(filename)}`),
    { headers: authHeaders(token) }
  );

  if (!data.url) {
    throw new Error("Artifact URL missing from response.");
  }

  return { url: data.url, filename: data.filename };
};

/**
 * A minimal stand-in for the web app's TanStack `useArtifactLinkMutation`,
 * so `generated-downloads.tsx` stays close to verbatim.
 * @param options - The token and which link to mint.
 * @returns `mutateAsync`, `isPending` and `reset`, as the mutation exposes.
 */
export const useArtifactLinkMutation = ({
  token,
}: {
  token: string | null | undefined;
  action: "preview" | "download";
}) => {
  const [isPending, setIsPending] = useState(false);
  const mutateAsync = useCallback(
    async (input: ThreadFileInput) => {
      setIsPending(true);
      try {
        return await createArtifactLink({ ...input, token });
      } finally {
        setIsPending(false);
      }
    },
    [token]
  );
  const reset = useCallback(() => setIsPending(false), []);
  return { mutateAsync, isPending, reset };
};

export const createUploadedFileLink = async ({
  threadId,
  filename,
  token,
}: ThreadFileInput & {
  token: string | null | undefined;
}): Promise<AgentFileLink> => {
  const { data } = await axios.get<Partial<AgentFileLink>>(
    agentsApiUrl(`/agents/threads/${encodeURIComponent(threadId)}/uploads?filename=${encodeURIComponent(filename)}`),
    { headers: authHeaders(token) }
  );

  if (!data.url) {
    throw new Error("Upload URL missing from response.");
  }

  return { url: data.url, filename: data.filename };
};
